/**
 * i18n Configuration
 *
 * Internationalization setup using i18next with react-i18next integration.
 * All configuration is sourced from ConfigurationService following the project's
 * 100% dynamic configuration mandate.
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import translationEN from './locales/en/translation.json';
import translationAM from './locales/am/translation.json';
import translationOM from './locales/om/translation.json';
import translationSO from './locales/so/translation.json';
import translationTI from './locales/ti/translation.json';

// ============================================================================
// Language Resources
// ============================================================================

/**
 * Translation resources for all supported languages.
 * New languages can be added by:
 * 1. Creating a new translation file at src/locales/{lang}/translation.json
 * 2. Importing it here
 * 3. Adding it to the resources object
 * 4. Updating VITE_I18N_SUPPORTED_LANGUAGES in .env
 */
const resources = {
    en: {
        translation: translationEN,
    },
    am: {
        translation: translationAM,
    },
    om: {
        translation: translationOM,
    },
    so: {
        translation: translationSO,
    },
    ti: {
        translation: translationTI,
    },
};

// ============================================================================
// i18n Initialization
// ============================================================================

// i18n configuration with safe fallbacks for startup.
// Explicitly calling getters after bootstrap is handled by components.
const defaultLanguage = 'en';
const fallbackLanguage = 'en';

i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
        resources,
        lng: defaultLanguage,
        fallbackLng: fallbackLanguage,
        debug: import.meta.env.DEV,
        interpolation: {
            // React already escapes values, so we disable double-escaping
            escapeValue: false,
        },
        detection: {
            // Order of language detection methods
            order: ['localStorage', 'navigator', 'htmlTag'],
            // Cache user language preference
            caches: ['localStorage'],
            // Key for localStorage
            lookupLocalStorage: 'i18nextLng',
        },
        // Return empty string for missing translations (allows fallback)
        returnEmptyString: false,
        // React-specific options
        react: {
            useSuspense: true,
        },
    });

export default i18n;
